import { formatDistanceToNow, isValid } from "date-fns";

export const DEFAULT_APP_TIMEZONE = "America/New_York";

let currentTimezone = DEFAULT_APP_TIMEZONE;

const ZERO_DATE_VALUES = new Set(["0000-00-00", "0000-00-00 00:00:00"]);
const NAIVE_DB_DATETIME_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?$/;
const ISO_DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function getAppDisplayTimezone() {
  return currentTimezone || DEFAULT_APP_TIMEZONE;
}

export function setAppDisplayTimezone(timezone) {
  const tz = String(timezone || "").trim();
  if (!tz) {
    currentTimezone = DEFAULT_APP_TIMEZONE;
    return currentTimezone;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    currentTimezone = tz;
  } catch {
    currentTimezone = DEFAULT_APP_TIMEZONE;
  }
  return currentTimezone;
}

/**
 * Parse API/database instants (modify_date, sign-in, shift start/end, etc.).
 * Naive "YYYY-MM-DD HH:mm:ss" values from MySQL are UTC wall clock (see backend db timezone: Z).
 */
export function parseDbDateTime(value) {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw || ZERO_DATE_VALUES.has(raw)) return null;

  if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const naive = raw.match(NAIVE_DB_DATETIME_RE);
  if (naive) {
    const parsed = new Date(`${naive[1]}T${naive[2]}${naive[3] || ""}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dateOnly = raw.match(ISO_DATE_ONLY_RE);
  if (dateOnly) {
    const parsed = new Date(`${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}T12:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = /^\d+$/.test(raw) ? new Date(Number(raw)) : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Resolve string inputs as DB instants; leave Date objects as-is. */
export function resolveAppInstant(value) {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    return parseDbDateTime(value);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function normalizeDateInput(value) {
  return resolveAppInstant(value);
}

function partsMap(parts) {
  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });
  return map;
}

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Build a Date instant from wall-clock components in the app display timezone.
 * Inverse of getAppWallDateParts (iterative refinement for DST edges).
 */
export function instantFromAppWallParts(parts, timeZone = getAppDisplayTimezone()) {
  if (!parts) return null;
  const { year, month, day, hour, minute, second = 0 } = parts;
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  let guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  for (let i = 0; i < 6; i += 1) {
    const wall = getAppWallDateParts(guess, timeZone);
    if (!wall) break;
    if (
      wall.year === year &&
      wall.month === month &&
      wall.day === day &&
      wall.hour === hour &&
      wall.minute === minute
    ) {
      return guess;
    }
    const targetMs = Date.UTC(year, month - 1, day, hour, minute, second);
    const wallMs = Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second ?? 0,
    );
    guess = new Date(guess.getTime() + (targetMs - wallMs));
  }
  return guess;
}

/** MySQL naive string using UTC wall components (inverse of parseDbDateTime). */
export function toMysqlUtcWallString(instant) {
  const date = resolveAppInstant(instant);
  if (!date) return "";
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;
}

/** App-wall "YYYY-MM-DDTHH:mm" for picker/modal state (not browser local). */
export function toDatetimeLocalValue(instant, timeZone = getAppDisplayTimezone()) {
  const parts = getAppWallDateParts(instant, timeZone);
  if (!parts) return "";
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

/** Parse app-wall "YYYY-MM-DDTHH:mm" to a Date instant. */
export function fromDatetimeLocalValue(str, timeZone = getAppDisplayTimezone()) {
  const raw = String(str || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return null;
  return instantFromAppWallParts(
    {
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      hour: Number(m[4]),
      minute: Number(m[5]),
    },
    timeZone,
  );
}

export function getAppWallDateParts(instant, timeZone = getAppDisplayTimezone()) {
  const date = instant instanceof Date ? instant : parseDbDateTime(instant);
  if (!date || Number.isNaN(date.getTime())) return null;

  const map = partsMap(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date),
  );

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second ?? 0),
  };
}

export function toAppDateKey(instant, timeZone = getAppDisplayTimezone()) {
  const parts = getAppWallDateParts(instant, timeZone);
  if (!parts) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function getAppTodayIso(timeZone = getAppDisplayTimezone()) {
  return toAppDateKey(new Date(), timeZone);
}

export function addAppCalendarDays(isoDate, delta, timeZone = getAppDisplayTimezone()) {
  const match = String(isoDate || "").match(ISO_DATE_ONLY_RE);
  if (!match) return isoDate;
  const anchor = parseDbDateTime(`${match[1]}-${match[2]}-${match[3]}T12:00:00Z`);
  if (!anchor) return isoDate;
  anchor.setUTCDate(anchor.getUTCDate() + Number(delta || 0));
  return toAppDateKey(anchor, timeZone);
}

/** Monday-start week containing isoDate (calendar date in app TZ). */
export function getAppWeekStartIso(isoDate, timeZone = getAppDisplayTimezone()) {
  const key = String(isoDate || "").match(ISO_DATE_ONLY_RE)
    ? isoDate
    : toAppDateKey(isoDate, timeZone);
  const anchor = parseDbDateTime(`${key}T12:00:00Z`);
  if (!anchor) return key;
  const parts = getAppWallDateParts(anchor, timeZone);
  if (!parts) return key;
  const jsDay = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  const daysFromMonday = (jsDay + 6) % 7;
  return addAppCalendarDays(key, -daysFromMonday, timeZone);
}

export function formatAppIntlLabel(
  instant,
  intlOptions,
  options = {},
) {
  const { emptyValue = "", timeZone = getAppDisplayTimezone() } = options;
  const date = resolveAppInstant(instant);
  if (!date) return emptyValue;
  return new Intl.DateTimeFormat("en-US", { ...intlOptions, timeZone }).format(date);
}

export function formatAppWeekdayShort(value, options = {}) {
  return formatAppIntlLabel(value, { weekday: "short" }, options);
}

export function formatAppMonthLong(value, options = {}) {
  return formatAppIntlLabel(value, { month: "long" }, options);
}

export function formatAppDistanceToNow(value, options = {}) {
  const { addSuffix = true, emptyValue = "" } = options;
  const date = parseDbDateTime(value);
  if (!date || !isValid(date)) return emptyValue;
  return formatDistanceToNow(date, { addSuffix });
}

/** Jobs-style compact relative time for assignment last-modified rows. */
export function formatAppRelativeShortAgo(value, options = {}) {
  const { emptyValue = "No date available" } = options;
  const date = parseDbDateTime(value);
  if (!date || !isValid(date)) return emptyValue;

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return "just now";

  const diffMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMins / 60);
  const minutes = diffMins % 60;
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return days === 1
      ? "1 day ago"
      : remainingHours > 0
        ? `${days} days ${remainingHours}h ago`
        : `${days} days ago`;
  }

  return hours > 0 ? `${hours}h ${minutes}m ago` : `${minutes}m ago`;
}

export function formatAppDateTime(value, options = {}) {
  const {
    emptyValue = "",
    includeDate = true,
    timeZone = getAppDisplayTimezone(),
  } = options;
  const date = resolveAppInstant(value);
  if (!date) return emptyValue;

  const parts = new Intl.DateTimeFormat("en-US", {
    month: includeDate ? "short" : undefined,
    day: includeDate ? "numeric" : undefined,
    year: includeDate ? "numeric" : undefined,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).formatToParts(date);

  const map = partsMap(parts);

  const timeText = `${map.hour || ""}:${map.minute || "00"} ${(
    map.dayPeriod || ""
  ).toLowerCase()}`.trim();

  if (!includeDate) return timeText;

  return `${map.month || ""} ${map.day || ""}, ${map.year || ""} ${timeText}`.trim();
}

export function formatAppDate(value, options = {}) {
  const { emptyValue = "", timeZone = getAppDisplayTimezone() } = options;
  const date = resolveAppInstant(value);
  if (!date) return emptyValue;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });
}

export function formatAppTime(value, options = {}) {
  const { emptyValue = "", timeZone = getAppDisplayTimezone() } = options;
  const date = resolveAppInstant(value);
  if (!date) return emptyValue;
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
}

export function hasAppDateTime(value) {
  return resolveAppInstant(value) !== null;
}

/** @deprecated use getAppTodayIso */
export function getTodayDateString() {
  return getAppTodayIso();
}
